import { searchGroupMembers } from 'src/models/group/group';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';

export interface MentionSearchUser {
  id: Identifier;
  name: string;
  avatar?: string;
}

interface AutocompleteUserRow {
  id: Identifier;
  term: string;
  profile_image?: string | null;
}

/**
 * User autocomplete for @mentions on activity (non-group): platform public users.
 */
export const searchMentionableUsers = async ({
  activityUserId,
  term,
  signal,
}: {
  activityUserId?: Identifier | null;
  term: string;
  signal?: AbortSignal;
}): Promise<MentionSearchUser[]> => {
  const type = 'platform_users';
  const { data } = await api.get<{
    results: Record<string, AutocompleteUserRow[]>;
  }>('/api/v4/autocomplete', {
    params: {
      types: [type],
      term,
      limit: 10,
      no_limit: false,
      page: 1,
      ...(activityUserId != null &&
        activityUserId !== '' && { activity_user_id: activityUserId }),
    },
    signal,
  });

  const rows = data.results[type] ?? [];
  return rows.map((r) => ({
    id: r.id,
    name: r.term,
    avatar: r.profile_image ?? undefined,
  }));
};

/**
 * @mention autocomplete limited to members of a group (group discussion / group activity).
 */
export const searchMentionableGroupMembers = async ({
  groupId,
  term,
  signal,
}: {
  groupId: Identifier | null | undefined;
  term: string;
  signal?: AbortSignal;
}): Promise<MentionSearchUser[]> => {
  if (groupId === null || groupId === undefined || groupId === '') {
    return [];
  }
  const typeKey = `group_members_${groupId}`;
  const rows = await searchGroupMembers({
    search: term,
    types: [typeKey],
    signal,
  });
  return rows.map((r) => ({
    id: r.id,
    name: r.name,
    avatar: r.image || undefined,
  }));
};
